import { Checkbox, ListItemText, MenuItem, OutlinedInput } from '@mui/material';
import React from 'react';
import { IoIosArrowDown } from 'react-icons/io';
import { useTranslation } from 'react-i18next';
import { StyledSelect } from './StyledComponents';
import Divider from '@mui/material/Divider';


const CustomSelect = ({
   className = '',
   name,
   id,
   multiple = false,
   value = multiple ? [] : '',
   onChange = () => { },
   options,
   dataType,
   defaultValue,
   readOnly,
   position,
   disabledUserIds,
   renderOption,
   ...rest
}) => {
   const { t, i18n } = useTranslation();

   const getValue = (option) => {
      if (!option) return '';
      switch (dataType) {
         case 'leaveType':
            return option.leaveTypeId;
         case 'userGroup':
            return option.groupCode;
         case 'shift':
            return option.shiftId;
         case 'country':
            return option.alpha3Code;
         case 'administratorRights':
            return option.roleName;
         case 'company':
            return option.companyId;
         case 'dropDownShowTime':
            return option.showTimeID;
         case 'userType':
            return option.value;
         case 'user':
            return option.userId;
         case 'group':
            return option.id;
         case 'toGroup':
            return option.groupId;
         case 'fromGroup':
            return option.groupId;
         case 'department':
            return option.departmentPrefix;
         case 'device':
            return option.deviceId;
         case 'selecStatusLeave':
            return option.statusLeaveId;
         case 'statusShiftRequestId':
            return option.statusShiftRequestId;
         case 'leavePolicy':
            return option.leavePolicyId;
         case 'designationId':
            return option.id;
         case 'joinDate':
            return option.value;
         case 'statusEmail':
            return option.statusEmailId;
         case 'visitors':
            return option.visitorId;
         default:
            return option.value;
      }
   };

   const getLabel = (option) => {
      switch (dataType) {
         case "leaveType":
            if (dataType === "leaveType") {
               const langId = Number(localStorage.getItem("i18nextLngId")) || 1;

               // Ưu tiên theo ngôn ngữ
               if (langId === 1) {
                  return option?.remark || option?.leaveTypeName || "-";
               }

               if (langId === 2) {
                  return option?.remarkEN || option?.leaveTypeName || "-";
               }

               return option?.leaveTypeName || "-";
            }

            return option?.label || "-";
         case 'userGroup':
            return option.description;
         case 'shift':
            return option.shiftName === "DEFAULT"
               ? t("common.default")
               : option.shiftName;
         case 'country':
            return option.countryName;
         case 'administratorRights':
            return t(`roles.${option.remark}`);
         case 'company':
            return option.companyName;
         case 'dropDownShowTime':
            return option.description;
         case 'user':
            return option.fullName;
         case 'group':
            if (!option?.groupName) return "";

            const name = option.groupName.toUpperCase();

            if (name === "ALL USERS") {
               return t("common.all-user");
            }

            return option.groupName;
         case 'designationId':
            return option.designationName;
         case 'department':
            return option.departmentName;
         case 'device':
            return option.description
         case 'selecStatusLeave':
            return option.statusName;
         case 'statusShiftRequestId':
            return option.statusShiftRequestName;
         case 'toGroup':
         case 'fromGroup':
            if (!option?.groupName) return "";
            return option.groupName;
         case 'eventStatusId':
            return option.eventStatusName;
         case 'leavePolicy':
            return option.leavePolicyName === "NORMAL"
               ? t("common.normal")
               : t("common.restricted");
         case 'joinDate':
            return option.label;
         case 'statusEmail':
            return option.statusName;
         case 'visitors':
            return option.fullName;
         default:
            return option.label;
      }
   };

   const handleChange = (event) => {
      const { value } = event.target;


      if (multiple) {
         onChange({
            target: {
               name,
               value: typeof value === 'string' ? value.split(',') : value,
            },
         });
      } else {
         // Single selection, pass the value directly
         onChange({
            target: {
               name,
               value,
            },
         });
      }
   };



   // const validValue = options?.some(option => getValue(option) === value) ? value : '';
   const validValue = multiple
      ? Array.isArray(value)
         ? value.filter((val) => options?.some((option) => getValue(option) === val))
         : [] // If it's not an array, default to an empty array
      : options?.some((option) => getValue(option) === value)
         ? value
         : '';

   const readOnlySx = readOnly
      ? {
         "& .MuiSelect-select": {
            backgroundColor: "#E5E7EB",   // nền xám
            color: "#111827",
            WebkitTextFillColor: "#111827", // fix Chrome
         },

         // Overwrite border khi disabled
         "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#E5E7EB !important",
         },
         "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: "#E5E7EB !important",
         },

         // Chữ không bị mờ khi disabled
         "&.Mui-disabled": {
            color: "#111827",
            WebkitTextFillColor: "#111827",
         },
         "& .MuiOutlinedInput-root": {
            border: "1px solid #E5E7EB !important",
         },
      }
      : {};



   const renderSelectedLabel = (label) => {
      if (!label) return "";
      return (
         <span
            title={String(label)}
            className="block w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
         >
            {label}
         </span>
      );
   };

   return (
      <StyledSelect
         {...rest}
         multiple={multiple}
         IconComponent={(props) => (
            <IoIosArrowDown {...props} size={16} />
         )}
         renderValue={(selected) => {
            if (Array.isArray(selected)) {
               const labels = selected
                  .map((val) => {
                     const foundOption = options.find(
                        (option) => getValue(option) === val
                     );
                     return foundOption ? getLabel(foundOption) : "";
                  })
                  .filter((label) => label)
                  .join(", ");
               return renderSelectedLabel(labels);
            }
            const foundOption = options.find(
               (option) => getValue(option) === selected
            );
            const label = foundOption ? getLabel(foundOption) : selected;
            return renderSelectedLabel(label);
         }}
         name={name}
         id={id}
         value={validValue}
         // ❗ readOnly thì không cho đổi, không truyền handleChange
         onChange={readOnly ? undefined : handleChange}
         defaultValue={defaultValue || null}
         // ❗ disable Select để không mở dropdown
         disabled={false}
         readOnly={readOnly}
         className={
            `form-control h-[40px] ${className} `
         }
         sx={{
            "&&": {
               borderRadius: "10px",
            },
            // ✅ luôn trắng (dù readOnly hay không)
            "& .MuiOutlinedInput-root": {
               backgroundColor: "#fff",
               overflow: "hidden",
               borderRadius: "10px",
            },

            "& .MuiOutlinedInput-notchedOutline": {
               borderRadius: "10px",
            },

            "& .MuiSelect-select": {
               backgroundColor: "#fff",
               overflow: "hidden",
               textOverflow: "ellipsis",
               whiteSpace: "nowrap",
               display: "block",
               minWidth: 0,
               paddingRight: "36px !important",
               boxSizing: "border-box",
            },

            "& .MuiSelect-icon": {
               right: "8px",
               color: "#374151",
            },

            // Bỏ hiệu ứng hover/focus (override StyledSelect)
            "&:hover fieldset": {
               border: "1px solid #E5E7EB !important",
               boxShadow: "none !important",
            },
            "&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused fieldset": {
               border: "1px solid #E5E7EB !important",
               boxShadow: "none !important",
            },
            "&.Mui-focused, & .MuiOutlinedInput-root.Mui-focused": {
               boxShadow: "none !important",
            },

            ...readOnlySx,
         }}
         MenuProps={{
            anchorOrigin: {
               vertical: position === 'top' ? 'top' : 'bottom',
               horizontal: 'left',
            },
            transformOrigin: {
               vertical: position === 'top' ? 'bottom' : 'top',
               horizontal: 'left',
            },
            PaperProps: {
               style: {
                  maxHeight: 200,
               },
            },
            disableAutoFocusItem: true,
         }}
      >
         {options?.map((option, index) => (
            <MenuItem
               key={index}
               value={getValue(option)}
               disabled={option.disabled}
               disableRipple
               sx={{
                  minHeight: 42,
                  paddingTop: "10px",
                  paddingBottom: "10px",
                  "&:hover, &.Mui-focused, &.Mui-selected, &.Mui-selected:hover": {
                     backgroundColor: "transparent !important",
                  },
               }}
            >
               {multiple && (
                  <Checkbox
                     checked={
                        Array.isArray(value) && value.includes(getValue(option))
                     }
                  />
               )}

               {renderOption ? (
                  renderOption(option)
               ) : (
                  <ListItemText primary={getLabel(option)} />
               )}
            </MenuItem>
         ))}
      </StyledSelect>

   );
};

export default CustomSelect;
